import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { PLACEHOLDER_TEMPLATE_ID } from '../../../globals';
import { AccountService } from '../../core/account';
import { ApiErrorFactory } from '../../core/api-errors';
import { ObjectCookie } from '../../core/cookie';
import { DefaultDomainUtility } from '../../core/domain-utility';
import { SiteCreateMessage, SiteService } from '../../core/site';
import { SiteCreateResponseV2 } from '../../core/site/apisite-create';
import { Template, TemplateApiService, TemplateLookupResponse } from '../../core/template';
import { TemplateLookupMessage } from '../../core/template/template';
import { IsPartner, UserService } from '../../core/user.service';
import { FormFields } from '../create-progress/form-fields';
import {
  ecommerceTemplates,
  vcor,
  vwct,
  WSP_MANAGING_DISTRIBUTED_TEMPLATES_FLAG,
} from './create-from-template.constants';
import { FeatureFlagService } from '../../core/feature-flag.service';

@Injectable()
export class CreateFromTemplateService {
  protected marketingTemplates$$: BehaviorSubject<Template[]> = new BehaviorSubject(undefined);
  readonly marketingTemplates$: Observable<Template[]> = this.marketingTemplates$$.asObservable();

  protected partnerTemplates$$: BehaviorSubject<Template[]> = new BehaviorSubject(undefined);
  readonly partnerTemplates$: Observable<Template[]> = this.partnerTemplates$$.asObservable();

  private easyEcommerceTemplates$$: BehaviorSubject<Template[]> = new BehaviorSubject(undefined);
  readonly easyEcommerceTemplates$: Observable<Template[]> = this.easyEcommerceTemplates$$.asObservable();

  private standardTemplates$$: BehaviorSubject<Template[]> = new BehaviorSubject(undefined);
  readonly standardTemplates$: Observable<Template[]> = this.standardTemplates$$.asObservable();

  private nonDistributedTemplates$$: BehaviorSubject<Template[]> = new BehaviorSubject(undefined);
  readonly nonDistributedTemplates$: Observable<Template[]> = this.nonDistributedTemplates$$.asObservable();

  form$$: BehaviorSubject<FormFields> = new BehaviorSubject<FormFields>(undefined);
  accountGroupId: string;
  readonly createDisabled$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private templateId$$: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);
  private templateLookupPartnerIds: string[] = [];
  private loading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private templateLoading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private isSuperAdmin$$ = new BehaviorSubject<boolean>(false);
  private isSmbUser = false;
  private isDistributedTemplatesFeatureEnabled$$ = new BehaviorSubject<boolean>(false);
  readonly templateLoading$: Observable<boolean> = this.templateLoading$$.asObservable();
  readonly isSuperAdmin$: Observable<boolean> = this.isSuperAdmin$$.asObservable();
  readonly isDistributedTemplatesFeatureEnabled$: Observable<boolean> =
    this.isDistributedTemplatesFeatureEnabled$$.asObservable();
  private cookie: ObjectCookie;

  constructor(
    protected alertService: SnackbarService,
    protected apiErrorFactory: ApiErrorFactory,
    private templateService: TemplateApiService,
    private accountService: AccountService,
    private route: ActivatedRoute,
    private router: Router,
    private siteService: SiteService,
    protected userService: UserService,
    private translateService: TranslateService,
    private featureFlagService: FeatureFlagService,
  ) {
    this.templateLookupPartnerIds = [vcor];
    this.setTemplateSites();
  }

  // Do not use independently from setTemplateSites
  private lookupTemplates(partnerIds: string[], includeUnpublished: boolean): Observable<TemplateLookupResponse> {
    const lookupMessage = new TemplateLookupMessage(partnerIds, includeUnpublished, '', false);
    return this.templateService.lookup(lookupMessage).pipe(
      catchError((err) => {
        this.handleError(err);
        return of(err);
      }),
    );
  }

  // Do not use independently from setTemplateSites
  // calls lookupTemplates with the templateLookupPartnerIDs set in SetTemplateSites
  // then sets the template category lists according to fiters and sets templateLoading to false
  private loadTemplates(): void {
    this.lookupTemplates(this.templateLookupPartnerIds, false)
      .pipe(
        filter((templateResponse) => !!templateResponse),
        map((templateResponse) => templateResponse.templates),
      )
      .subscribe((templateList) => {
        const isDistributedTemplatesFeatureEnabled = this.isDistributedTemplatesFeatureEnabled$$.value;
        const shouldFilterByDistribution = isDistributedTemplatesFeatureEnabled && this.isSmbUser;
        const filteredTemplates = shouldFilterByDistribution
          ? templateList.filter((t) => t.isTemplateDistributed)
          : templateList;
        const marketingTemplates = filteredTemplates.filter((t) => t.partnerId === vwct);
        const standardTemplates = filteredTemplates.filter(
          (t) => t.partnerId === vcor && !ecommerceTemplates.has(t.siteId),
        );
        const easyEcommerceTemplates = filteredTemplates.filter((t) => ecommerceTemplates.has(t.siteId));

        const nonDistributedTemplates =
          !this.isSmbUser && isDistributedTemplatesFeatureEnabled
            ? templateList.filter((t) => !t.isTemplateDistributed && t.partnerId !== vcor && t.partnerId !== vwct)
            : [];

        const partnerTemplates =
          !this.isSmbUser && isDistributedTemplatesFeatureEnabled
            ? filteredTemplates.filter((t) => t.partnerId !== vcor && t.partnerId !== vwct && t.isTemplateDistributed)
            : filteredTemplates.filter((t) => t.partnerId !== vcor && t.partnerId !== vwct);

        this.marketingTemplates$$.next(marketingTemplates);
        this.partnerTemplates$$.next(partnerTemplates);
        this.standardTemplates$$.next(standardTemplates);
        this.easyEcommerceTemplates$$.next(easyEcommerceTemplates);
        this.nonDistributedTemplates$$.next(nonDistributedTemplates);
        this.templateLoading$$.next(false);
      });
  }

  // subscribes to accountService and iamService to add the partnerId and conditionally VWCT to templateLookupPartnerIds
  // checks feature flag for distributed templates and then calls loadTemplates
  setTemplateSites(): void {
    combineLatest([
      // distinctUntilChanged included to limit number of requests for the same templates (both emit more than once)
      this.accountService.result().pipe(distinctUntilChanged((prev, cur) => prev.partnerId === cur.partnerId)),
      this.userService.isSuperAdmin$,
      this.userService.isPartner$,
    ])
      .pipe(
        switchMap(([acc, isSuperAdmin, isPartner]) => {
          this.templateLookupPartnerIds.push(acc.partnerId);
          // Check if user is an SMB user (not a partner)
          this.isSmbUser = isPartner === IsPartner.No;
          if (isSuperAdmin) {
            this.isSuperAdmin$$.next(true);
            this.templateLookupPartnerIds.push(vwct);
          }
          // Check if the distributed templates feature flag is enabled for this partner
          return this.featureFlagService
            .isFeatureEnabled(acc.partnerId, WSP_MANAGING_DISTRIBUTED_TEMPLATES_FLAG)
            .pipe(map((isEnabled) => isEnabled ?? false));
        }),
      )
      .subscribe((isFeatureEnabled) => {
        this.isDistributedTemplatesFeatureEnabled$$.next(isFeatureEnabled);
        this.loadTemplates();
      });
  }

  setFormFields(form: FormFields, isPlaceholder: boolean): Observable<SiteCreateResponseV2> {
    this.cookie.set(form);
    this.form$$.next(form);
    return this.createSite(isPlaceholder);
  }

  setTemplate(template: Template): void {
    this.templateId$$.next(template.siteId);
  }

  createSite(isPlaceholder?: boolean): Observable<SiteCreateResponseV2> {
    const form = this.cookie.get() as FormFields;
    const newForm = new FormFields();
    newForm.siteTagline = form.siteTagline;
    newForm.siteTitle = form.siteTitle;
    newForm.defaultDomain = form.defaultDomain;
    newForm.multisite = form.multisite;
    let templateId = this.templateId$$.value;
    if (isPlaceholder) {
      templateId = PLACEHOLDER_TEMPLATE_ID;
    }
    const site = newForm.buildSiteCreateMessage(this.accountGroupId, templateId);
    return this.createAsync(site);
  }

  initFormFields(): void {
    this.loading$$.next(true);
    const form = new FormFields();
    if (this.siteService.hasCachedCreateMessage()) {
      form.populateFromSiteCreateMessage(this.siteService.cachedCreateMessage);
      return;
    }
    this.accountService.fetch(this.accountGroupId);
    this.accountService.result().subscribe((account) => {
      form.siteTitle = account.companyName;
      form.siteTagline = account.companyName;
      form.defaultDomain = DefaultDomainUtility.convertToDomainLabel(account.companyName);
    });
    this.accountService.error().subscribe((err) => {
      if (err.statusInt === 404 || err.statusInt === 403) {
        this.navigateByUrl('/app-failure/We%20can%27t%20find%20your%20account.');
      }
    });
    this.form$$.next(form);
    this.loading$$.next(false);
  }

  setCreateDisabled(b: boolean): void {
    this.createDisabled$$.next(b);
  }

  setLoading(b: boolean): void {
    this.loading$$.next(b);
  }

  navigateByUrl(url: string): void {
    this.router.navigateByUrl(url);
  }

  setAccountGroupId(id: string): void {
    this.accountGroupId = id;
    this.cookie = new ObjectCookie(this.accountGroupId + 'CreateForm');
  }

  loading$(): Observable<boolean> {
    return this.loading$$.asObservable();
  }

  private createAsync(site: SiteCreateMessage): Observable<SiteCreateResponseV2> {
    return this.siteService.createAsync(site);
  }

  handleError(errorResponse: HttpErrorResponse): void {
    const parsedError = this.apiErrorFactory.parseErrorResponse(errorResponse);
    this.displayError(parsedError.getUserMessage());
  }

  displayError(error: string): void {
    this.alertService.openErrorSnack(error, this.translateService.instant('ALERTS.GENERAL.DISMISS'));
  }
}
